/**
 * Responsive Media Component
 * Prevents double-downloading of media by loading only the viewport-appropriate
 * variant (desktop or mobile). Media is placed in inert <template> tags —
 * the browser does not download resources inside templates until they are
 * cloned into the DOM.
 *
 * Usage:
 *   <fx-responsive-media data-breakpoint="799">
 *     <picture data-poster>…poster img…</picture>
 *     <template data-desktop>…video/iframe markup…</template>
 *     <template data-mobile>…video/iframe markup…</template>
 *   </fx-responsive-media>
 *
 * Poster and video are stacked via CSS Grid (grid-area: 1/1) so both occupy
 * the same space. The poster stays on top (z-index) until the video is
 * playing, then it is removed. No absolute positioning needed.
 */
class FXResponsiveMedia extends HTMLElement {
  connectedCallback() {
    const breakpoint = parseInt(this.dataset.breakpoint) || 799;
    const isMobile = window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches;
    const preferred = this.querySelector(isMobile ? 'template[data-mobile]' : 'template[data-desktop]');
    const fallback = this.querySelector(isMobile ? 'template[data-desktop]' : 'template[data-mobile]');
    const template = preferred || fallback;
    if (template) {
      this.appendChild(template.content.cloneNode(true));
    }
    this.querySelectorAll('template').forEach(t => t.remove());

    const poster = this.querySelector('[data-poster]');
    if (!poster) return;

    // Remove poster once media is ready to play
    const video = this.querySelector('video');
    const iframe = this.querySelector('iframe');

    if (video) {
      // Muted autoplay videos may already be playing before listener attaches
      if (!video.paused) {
        poster.remove();
      } else {
        video.addEventListener('playing', () => poster.remove(), { once: true });
        // iOS Safari and some Android browsers don't honor the autoplay attribute
        // on <video> elements cloned from <template> post-load. Kick playback
        // explicitly; the promise rejects silently if the browser blocks it.
        const playAttempt = video.play();
        if (playAttempt && typeof playAttempt.catch === 'function') {
          playAttempt.catch(() => {});
        }
      }
    } else if (iframe) {
      iframe.addEventListener('load', () => poster.remove(), { once: true });
    } else {
      poster.remove();
    }
  }
}

if (!customElements.get('fx-responsive-media')) {
  customElements.define('fx-responsive-media', FXResponsiveMedia);
}
